/**
 * ドイツ (メーカー) 報告記録のヘルパー (2026-08-24)。
 *
 * 報告対象 4 項目 (電話番号 / メールアドレス / 英語氏名 / 英語住所) について、
 *   - 現在の顧客データからスナップショットを組み立てる (buildGermanyReportSnapshot)
 *   - 現在データと直近報告の差異を項目ごとに判定する (diffGermanyReport)
 * を提供する。判定・表示ロジックを admin-web / functions で共用するための純関数群。
 *
 * データモデルの正: shared/src/types/mail.ts の GermanyReport / GermanyReportSnapshot。
 */
import { formatEnglishAddress } from './english-address';
import type {
  EnglishAddress,
  GermanyReportSnapshot,
  MailContact,
} from '../types/mail';

/** 報告対象 4 項目のキー (スナップショットのフィールド名と一致)。 */
export const GERMANY_REPORT_FIELD_KEYS = [
  'phone',
  'email',
  'nameEn',
  'englishAddress',
] as const;

export type GermanyReportFieldKey = (typeof GERMANY_REPORT_FIELD_KEYS)[number];

/** 画面表示用ラベル。 */
export const GERMANY_REPORT_FIELD_LABELS: Record<GermanyReportFieldKey, string> = {
  phone: '電話番号',
  email: 'メールアドレス',
  nameEn: '氏名（英語表記）',
  englishAddress: '住所（英語表記）',
};

function clean(v: string | null | undefined): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** 英語住所を null 正規化する (全フィールド空なら null)。 */
function normalizeEnglishAddress(
  a: EnglishAddress | null | undefined,
): EnglishAddress | null {
  if (!a) return null;
  const next: EnglishAddress = {
    country: clean(a.country) || null,
    postalCode: clean(a.postalCode) || null,
    stateRegion: clean(a.stateRegion) || null,
    city: clean(a.city) || null,
    line1: clean(a.line1) || null,
    line2: clean(a.line2) || null,
  };
  const hasAny =
    next.country ||
    next.postalCode ||
    next.stateRegion ||
    next.city ||
    next.line1 ||
    next.line2;
  return hasAny ? next : null;
}

/**
 * 現在の顧客データから報告スナップショットを組み立てる。
 * 「報告済みとして記録」操作時に、この戻り値をそのまま germany_reports へ凍結保存する。
 */
export function buildGermanyReportSnapshot(
  contact: Pick<MailContact, 'phone' | 'defaultEmail' | 'nameEn' | 'englishAddress'>,
): GermanyReportSnapshot {
  return {
    phone: clean(contact.phone) || null,
    email: clean(contact.defaultEmail).toLowerCase() || null,
    nameEn: clean(contact.nameEn) || null,
    englishAddress: normalizeEnglishAddress(contact.englishAddress),
  };
}

/** スナップショットの 1 項目を表示用文字列にする (空は '')。 */
export function formatGermanyReportValue(
  key: GermanyReportFieldKey,
  snapshot: GermanyReportSnapshot | null | undefined,
): string {
  if (!snapshot) return '';
  if (key === 'englishAddress') {
    return formatEnglishAddress(snapshot.englishAddress);
  }
  return clean(snapshot[key]);
}

/** 英語住所の同値判定 (表示順に依存しない、フィールド単位の正規化比較)。 */
function isSameEnglishAddress(
  a: EnglishAddress | null | undefined,
  b: EnglishAddress | null | undefined,
): boolean {
  const na = normalizeEnglishAddress(a);
  const nb = normalizeEnglishAddress(b);
  if (!na && !nb) return true;
  if (!na || !nb) return false;
  return (
    (na.country ?? '') === (nb.country ?? '') &&
    (na.postalCode ?? '') === (nb.postalCode ?? '') &&
    (na.stateRegion ?? '') === (nb.stateRegion ?? '') &&
    (na.city ?? '') === (nb.city ?? '') &&
    (na.line1 ?? '') === (nb.line1 ?? '') &&
    (na.line2 ?? '') === (nb.line2 ?? '')
  );
}

/** 差異判定の 1 項目分。 */
export interface GermanyReportFieldDiff {
  key: GermanyReportFieldKey;
  label: string;
  /** 現在の顧客データの表示値 ('' = 未登録)。 */
  current: string;
  /** 直近報告の表示値 ('' = 未報告 / 報告時空欄)。 */
  reported: string;
  /** 現在データと直近報告が一致しているか。 */
  matches: boolean;
}

/**
 * 現在の顧客データと直近報告スナップショットを項目ごとに比較する。
 * reported=null (未報告) のときも current を並べた配列を返す (matches は current が空の項目のみ true)。
 */
export function diffGermanyReport(
  contact: Pick<MailContact, 'phone' | 'defaultEmail' | 'nameEn' | 'englishAddress'>,
  reported: GermanyReportSnapshot | null | undefined,
): GermanyReportFieldDiff[] {
  const current = buildGermanyReportSnapshot(contact);
  return GERMANY_REPORT_FIELD_KEYS.map((key) => {
    const currentStr = formatGermanyReportValue(key, current);
    const reportedStr = formatGermanyReportValue(key, reported);
    const matches =
      key === 'englishAddress'
        ? isSameEnglishAddress(
            current.englishAddress,
            reported?.englishAddress ?? null,
          )
        : currentStr === reportedStr;
    return {
      key,
      label: GERMANY_REPORT_FIELD_LABELS[key],
      current: currentStr,
      reported: reportedStr,
      matches,
    };
  });
}

/** 全項目一致なら true (= 再報告不要)。 */
export function isGermanyReportUpToDate(
  diffs: GermanyReportFieldDiff[],
): boolean {
  return diffs.every((d) => d.matches);
}
